import { useState, createElement } from 'react'
import type { DocumentProps } from '@react-pdf/renderer'
import { toast } from 'sonner'
import { getAllProductsWithVariants, getCategoryBySlug, getCategories } from '@/lib/api'
import { siteConfig } from '@/data/siteConfig'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function asPdfDoc(el: unknown): import('react').ReactElement<DocumentProps> {
  return el as import('react').ReactElement<DocumentProps>
}

export function useCataloguePDF() {
  const [isGenerating, setIsGenerating] = useState(false)

  const downloadFile = (url: string, filename: string) => {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
  }

  const checkFileExists = async (url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' })
      return response.ok
    } catch (err) {
      return false
    }
  }

  async function generateFullCatalogue() {
    if (isGenerating) return
    
    // Check if full catalogue exists in config
    const { useStaticPdf, fullCataloguePdfUrl } = siteConfig.catalogue
    
    if (useStaticPdf && fullCataloguePdfUrl) {
      const toastId = toast.loading('Checking for latest catalogue...')
      const exists = await checkFileExists(fullCataloguePdfUrl)
      
      if (exists) {
        try {
          downloadFile(fullCataloguePdfUrl, 'jamnagar-metals-catalogue-2025.pdf')
          toast.success('Catalogue downloaded!', { id: toastId })
          return
        } catch (err) {
          console.error('Direct download failed, falling back to generation:', err)
          toast.dismiss(toastId)
        }
      } else {
        console.warn('Static catalogue file not found, switching to dynamic generation')
        toast.dismiss(toastId)
      }
    }

    setIsGenerating(true)
    const toastId = toast.loading('Preparing your catalogue...')
    try {
      const [{ pdf }, { CataloguePDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./CataloguePDF'),
      ])

      const [categories, products] = await Promise.all([
        getCategories(),
        getAllProductsWithVariants(),
      ])

      const doc = asPdfDoc(createElement(CataloguePDF, {
        categories,
        products,
        heroImageUrl: '/catalogue-hero-tower-bolts.webp',
      }))

      const blob = await pdf(doc).toBlob()
      const url = URL.createObjectURL(blob)
      downloadFile(url, 'jamnagar-metals-catalogue-2025.pdf')
      URL.revokeObjectURL(url)

      toast.success('Catalogue generated and downloaded!', { id: toastId })
    } catch (err) {
      console.error('Catalogue generation failed:', err)
      toast.error('Failed to generate catalogue. Please try again.', { id: toastId })
    } finally {
      setIsGenerating(false)
    }
  }

  async function generateCategoryCatalogue(categorySlug: string) {
    if (isGenerating) return
    
    const category = await getCategoryBySlug(categorySlug)
    if (!category) {
      toast.error('Category not found')
      return
    }

    // Direct download if pre-generated
    if (category.use_static_pdf && category.pdf_url) {
      const toastId = toast.loading(`Checking for ${category.name} catalogue...`)
      const exists = await checkFileExists(category.pdf_url)

      if (exists) {
        try {
          downloadFile(category.pdf_url, `${category.slug}-catalogue.pdf`)
          toast.success(`${category.name} catalogue downloaded!`, { id: toastId })
          return
        } catch (err) {
          console.error('Direct download failed:', err)
          toast.dismiss(toastId)
        }
      } else {
        console.warn(`Static catalogue for ${category.name} not found, switching to dynamic generation`)
        toast.dismiss(toastId)
      }
    }

    setIsGenerating(true)
    const toastId = toast.loading('Preparing category catalogue...')

    try {
      const [{ pdf }, { CategoryCataloguePDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./CategoryCataloguePDF'),
      ])

      const fullProducts = await getAllProductsWithVariants()
      const categoryProducts = fullProducts.filter((p) => p.category_id === category.id)

      const doc = asPdfDoc(createElement(CategoryCataloguePDF, {
        category,
        products: categoryProducts,
        heroImageUrl: category.image_url || '/hero.webp',
      }))

      const blob = await pdf(doc).toBlob()
      const url = URL.createObjectURL(blob)
      downloadFile(url, `jamnagar-metals-${categorySlug}-catalogue-2025.pdf`)
      URL.revokeObjectURL(url)

      toast.success(`${category.name} catalogue downloaded!`, { id: toastId })
    } catch (err) {
      console.error('Category catalogue generation failed:', err)
      toast.error('Failed to generate catalogue. Please try again.', { id: toastId })
    } finally {
      setIsGenerating(false)
    }
  }

  async function generateProductSheet(productSlug: string) {
    if (isGenerating) return
    setIsGenerating(true)
    const toastId = toast.loading('Preparing product sheet...')

    try {
      const [{ pdf }, { CategoryCataloguePDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./CategoryCataloguePDF'),
      ])

      const [allProducts, allCategories] = await Promise.all([
        getAllProductsWithVariants(),
        getCategories(),
      ])
      const product = allProducts.find((p) => p.slug === productSlug)
      if (!product) throw new Error('Product not found')

      const category = allCategories.find((c) => c.id === product.category_id)
      if (!category) throw new Error('Category not found')

      const doc = asPdfDoc(createElement(CategoryCataloguePDF, {
        category,
        products: [product],
        heroImageUrl: category.image_url || '/hero.webp',
      }))

      const blob = await pdf(doc).toBlob()
      const url = URL.createObjectURL(blob)
      downloadFile(url, `jamnagar-metals-${productSlug}-sheet.pdf`)
      URL.revokeObjectURL(url)

      toast.success('Product sheet downloaded!', { id: toastId })
    } catch (err) {
      console.error('Product sheet generation failed:', err)
      toast.error('Failed to generate product sheet. Please try again.', { id: toastId })
    } finally {
      setIsGenerating(false)
    }
  }

  return { generateFullCatalogue, generateCategoryCatalogue, generateProductSheet, isGenerating }
}
